import { useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import Scrollbar from "src/components/scrollbar";
import { fMessageTime } from "src/utils/formatChatTime";
import AnimatedCollapse from "../chat/room/AnimatedCollapse";
import ChatRoomCollapseButton from "../chat/room/ChatRoomCollapseButton";
import { getFunctionCallLabel } from "./functionCallLabels";

type RawMessage = any;

type FunctionCallLogEntry = {
  id: string;
  name: string;
  label: string;
  timestamp: any;
  args: any;
  result: any;
};

/**
 * Casa cada tool_call de uma mensagem `role: "assistant"` com o resultado
 * correspondente (`role: "tool"`, mesmo `tool_call_id`) e devolve a lista em
 * ordem cronológica — mesma fonte (`historicChat` bruto) que ChatMessageList
 * usa, sem precisar de endpoint novo.
 */
function buildFunctionCallLog(history: RawMessage[]): FunctionCallLogEntry[] {
  const toolResultById = new Map<string, any>();

  history.forEach((raw) => {
    if (raw?.role === "tool" && raw?.tool_call_id) {
      toolResultById.set(raw.tool_call_id, raw.content);
    }
  });

  const entries: FunctionCallLogEntry[] = [];

  history.forEach((raw, index) => {
    if (raw?.role !== "assistant" || !Array.isArray(raw.tool_calls)) return;

    raw.tool_calls.forEach((call: any) => {
      const name = call?.function?.name;
      if (!name) return;

      let args: any = call.function?.arguments;
      try {
        args = JSON.parse(call.function.arguments);
      } catch {
        // mantém a string crua se não for JSON válido
      }

      const rawResult = call.id ? toolResultById.get(call.id) : undefined;
      let result: any = rawResult;
      if (typeof rawResult === "string") {
        try {
          result = JSON.parse(rawResult);
        } catch {
          result = rawResult;
        }
      }

      entries.push({
        id: `${raw.timestamp ?? index}-${call.id ?? name}`,
        name,
        label: getFunctionCallLabel(name),
        timestamp: raw.timestamp ?? null,
        args,
        result,
      });
    });
  });

  return entries;
}

const preSx = {
  m: 0,
  p: 1.5,
  borderRadius: 1,
  bgcolor: "background.neutral",
  fontSize: 12,
  fontFamily: "monospace",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
};

type Props = {
  history: RawMessage[];
};

export default function FunctionCallLogList({ history }: Props) {
  const entries = useMemo(() => buildFunctionCallLog(history), [history]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!entries.length) {
    return (
      <Stack sx={{ p: 2, height: 1 }} justifyContent="center" alignItems="center">
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Nenhuma chamada de função nesta conversa ainda.
        </Typography>
      </Stack>
    );
  }

  return (
    <Scrollbar sx={{ height: 1 }}>
      {entries.map((entry) => (
        <Box
          key={entry.id}
          sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <ChatRoomCollapseButton
            isCollapse={expandedId === entry.id}
            onCollapse={() =>
              setExpandedId((prev) => (prev === entry.id ? null : entry.id))
            }
          >
            <Stack direction="row" justifyContent="space-between" flexGrow={1} sx={{ pr: 1 }}>
              <Typography variant="caption" sx={{ textTransform: "none", fontWeight: 600 }}>
                {entry.label}
              </Typography>
              {entry.timestamp && (
                <Typography variant="caption" color="text.disabled">
                  {fMessageTime(entry.timestamp)}
                </Typography>
              )}
            </Stack>
          </ChatRoomCollapseButton>

          <AnimatedCollapse in={expandedId === entry.id}>
            <Stack spacing={1.5} sx={{ p: 2, pt: 0 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Argumentos
                </Typography>
                <Box component="pre" sx={preSx}>
                  {JSON.stringify(entry.args, null, 2)}
                </Box>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Resultado
                </Typography>
                <Box component="pre" sx={preSx}>
                  {JSON.stringify(entry.result, null, 2)}
                </Box>
              </Stack>
            </Stack>
          </AnimatedCollapse>
        </Box>
      ))}
    </Scrollbar>
  );
}
